import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Plus, Search } from "lucide-react";
import type { Account } from "@/api/types";
import { useApi } from "@/context/ApiContext";
import { ExchangeMark, accountPill } from "@/components/ui/kit";
import { ActionsMenu } from "@/components/ActionsMenu";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import NewAccountModal from "@/components/exchange/NewAccountModal";
import { accountKindLabel, accountStatusView, accountUsdBalance, providerLabel } from "@/lib/account";
import { isAccountProviderEnabled, sortAccountProviders } from "@/lib/account-provider";
import { isLiveInstance } from "@/lib/live";
import { cn } from "@/lib/utils";
import { Pager } from "@/components/ui/Pager";
import { clampPage } from "@/lib/paging";
import { pageSizeFor, useGridColumns } from "@/hooks/useGridColumns";


type Filter = "all" | "connected" | "problems";

const money = new Intl.NumberFormat("en-US", { maximumFractionDigits: 0 });
const FALLBACK_PROVIDERS = ["binance", "mexc", "revolutx"];
const card = "flex min-w-0 flex-col gap-4 rounded-card border border-[var(--border)] bg-[var(--bg-card)] p-4";
const stat = "text-[11px] text-[var(--text-muted)]";

function isConnected(a: Account) {
  return accountStatusView(a.status, a.enabled).tone === "running";
}

/** Exchange accounts as cards (balance, positions, strategies), with "New account" in a dialog. */
export default function AccountsPage({ openNew = false }: { openNew?: boolean }) {
  const api = useApi(["accounts", "backtests"]);
  const navigate = useNavigate();
  const accounts: Account[] = useMemo(() => api.accounts || [], [api.accounts]);
  const [filter, setFilter] = useState<Filter>("all");
  const [q, setQ] = useState("");
  const [page, setPage] = useState(0);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<Account | null>(null);
  // The exchanges a new account can be on, for the "Connect an exchange" card.
  const [providers, setProviders] = useState<string[]>(FALLBACK_PROVIDERS);
  useEffect(() => {
    let cancelled = false;
    api.commands
      .listConfigurables()
      .then((list: { account_providers?: string[] } | null) => {
        if (!cancelled && list?.account_providers?.length) setProviders(sortAccountProviders(list.account_providers));
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [api.ready]);
  const connectable = [...new Set(providers.filter(isAccountProviderEnabled).map(providerLabel))];

  const liveCount = (id: string) =>
    (api.backtests || []).filter((bt) => isLiveInstance(bt) && bt.accounts?.some((a) => a.account_id === id)).length;

  const counts: Record<Filter, number> = useMemo(
    () => ({
      all: accounts.length,
      connected: accounts.filter(isConnected).length,
      problems: accounts.filter((a) => !isConnected(a)).length,
    }),
    [accounts]
  );

  const rows = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return accounts.filter((a) => {
      if (filter === "connected" && !isConnected(a)) return false;
      if (filter === "problems" && isConnected(a)) return false;
      if (!needle) return true;
      return a.name.toLowerCase().includes(needle) || providerLabel(a.provider).toLowerCase().includes(needle);
    });
  }, [accounts, filter, q]);
  // Cards in whole rows: a page is a few rows of however many fit across.
  const [gridRef, cols] = useGridColumns(3);
  const PAGE_SIZE = pageSizeFor(cols);
  const shownPage = clampPage(page, PAGE_SIZE, rows.length);
  const lastPage = shownPage === Math.max(0, Math.ceil(rows.length / PAGE_SIZE) - 1);

  async function run(id: string, fn: () => Promise<unknown>, fail: string) {
    setBusyId(id);
    setError(null);
    try {
      await fn();
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : fail);
    } finally {
      setBusyId(null);
    }
  }

  const reconnect = (a: Account) =>
    run(
      a.id,
      async () => {
        await api.commands.updateAccount({ id: a.id, enabled: true });
        await api.commands.restartAccount({ id: a.id });
      },
      "Reconnect failed"
    );
  const toggle = (a: Account, enabled: boolean) =>
    run(a.id, () => api.commands.updateAccount({ id: a.id, enabled }), "Couldn't update the account");

  const FILTERS: { id: Filter; label: string }[] = [
    { id: "all", label: "All" },
    { id: "connected", label: "Connected" },
    { id: "problems", label: "Not connected" },
  ];
  const deleteLive = deleteTarget ? liveCount(deleteTarget.id) : 0;

  return (
    <div className="flex min-w-0 flex-col gap-5">
      {/* No page title: the menu says where you are, so the tools take that row. */}
      <h1 className="sr-only">Accounts</h1>
      {error && (
        <div role="alert" className="rounded-[10px] bg-[var(--danger-bg)] px-3 py-2 text-[13px] text-[var(--danger)]">
          {error}
        </div>
      )}

      <div className="flex flex-wrap items-center justify-between gap-2.5">
        <div className="flex flex-wrap gap-1.5" role="group" aria-label="Filter accounts">
          {FILTERS.map((f) => (
            <button
              key={f.id}
              type="button"
              aria-pressed={filter === f.id}
              onClick={() => {
                setFilter(f.id);
                setPage(0);
              }}
              className={cn(
                "rounded-full border border-[var(--border)] px-2.5 py-[3px] text-xs",
                filter === f.id
                  ? "bg-[var(--bg-card-elevated)] text-[var(--text-primary)]"
                  : "bg-transparent text-[var(--text-muted)] hover:text-[var(--text-primary)]"
              )}
            >
              {f.label} <span className="num">{counts[f.id]}</span>
            </button>
          ))}
        </div>
        <div className="flex flex-wrap items-center gap-2">
        <label className="flex min-w-[220px] items-center gap-2 rounded-[10px] border border-[var(--border)] bg-[var(--bg-secondary)] px-2.5 py-1.5 text-[13px] text-[var(--text-disabled)] focus-within:border-[var(--primary)]">
          <Search size={14} className="shrink-0" />
          <input
            id="account-search"
            value={q}
            onChange={(e) => {
              setQ(e.target.value);
              setPage(0);
            }}
            placeholder="Search by name or exchange"
            className="!h-auto !min-h-0 w-full !border-0 !bg-transparent !p-0 !text-[13px] text-[var(--text-primary)] !shadow-none !outline-none placeholder:text-[var(--text-disabled)]"
          />
        </label>
          <button type="button" className="btn btn-primary" onClick={() => navigate("/integrations/marketplaces/new")}>
            + New account
          </button>
        </div>
      </div>

      {!api.ready ? (
        <div className="px-4 py-10 text-center text-[13px] text-[var(--text-muted)]">Loading accounts…</div>
      ) : accounts.length > 0 && rows.length === 0 ? (
        <div className="flex flex-col items-center gap-2.5 px-4 py-10 text-center text-[13px] text-[var(--text-muted)]">
          Nothing matches these filters.
          <button
            type="button"
            className="btn btn-ghost btn-sm"
            onClick={() => {
              setQ("");
              setFilter("all");
            }}
          >
            Clear filters
          </button>
        </div>
      ) : (
        <div ref={gridRef} className="grid gap-4 [grid-template-columns:repeat(auto-fill,minmax(300px,1fr))]">
          {rows.slice(shownPage * PAGE_SIZE, (shownPage + 1) * PAGE_SIZE).map((a) => {
            const balance = accountUsdBalance(api.balancesByAccount?.[a.id]);
            const positions = api.positionsByAccount?.[a.id]?.length ?? 0;
            const view = accountStatusView(a.status, a.enabled);
            const live = liveCount(a.id);
            const busy = busyId === a.id;
            return (
              <section key={a.id} aria-label={a.name} className={card}>
                <div className="flex items-start gap-3">
                  <ExchangeMark provider={a.provider} size={36} />
                  <div className="min-w-0 flex-1">
                    <Link
                      to={`/integrations/marketplaces/${a.id}`}
                      className="block truncate text-[15px] font-semibold text-[var(--text-primary)] no-underline hover:underline"
                    >
                      {a.name}
                    </Link>
                    <div className="truncate text-[13px] text-[var(--text-muted)]">
                      {providerLabel(a.provider)} · {accountKindLabel(a)}
                    </div>
                  </div>
                  <span className="shrink-0">{accountPill(a)}</span>
                </div>
                {view.tone === "error" && a.status_msg && (
                  <div className="-mt-1 truncate text-xs text-[var(--danger)]" title={a.status_msg}>
                    {a.status_msg}
                  </div>
                )}
                <div className="grid grid-cols-3 gap-2">
                  <div>
                    <div className={stat}>Balance</div>
                    <div className="num mt-0.5 text-[17px] font-semibold text-[var(--text-primary)]">
                      {balance ? (
                        <>
                          {money.format(balance.value)} <span className="text-xs font-normal text-[var(--text-muted)]">{balance.asset}</span>
                        </>
                      ) : (
                        "—"
                      )}
                    </div>
                  </div>
                  <div>
                    <div className={stat}>Positions</div>
                    <div className="num mt-0.5 text-[17px] font-semibold text-[var(--text-primary)]">{positions}</div>
                  </div>
                  <div>
                    <div className={stat}>Strategies</div>
                    <div className="num mt-0.5 text-[17px] font-semibold text-[var(--text-primary)]">{live}</div>
                  </div>
                </div>
                <div className="mt-auto flex items-center gap-2">
                  {a.enabled && (view.tone === "error" || view.tone === "stopped") ? (
                    <button type="button" className="btn btn-primary btn-sm flex-1" disabled={busy} onClick={() => void reconnect(a)}>
                      Reconnect
                    </button>
                  ) : !a.enabled ? (
                    <button type="button" className="btn btn-primary btn-sm flex-1" disabled={busy} onClick={() => void toggle(a, true)}>
                      Turn on
                    </button>
                  ) : (
                    <Link to={`/integrations/marketplaces/${a.id}`} className="btn btn-soft btn-sm flex-1">
                      Details
                    </Link>
                  )}
                  <Link to={`/integrations/marketplaces/${a.id}/edit`} className="btn btn-soft btn-sm flex-1">
                    Change keys
                  </Link>
                  <ActionsMenu
                    items={[
                      { label: "Open", href: `/integrations/marketplaces/${a.id}` },
                      ...(a.enabled ? [{ label: "Turn off", disabled: busy, onClick: () => void toggle(a, false) }] : []),
                      { label: "Delete", danger: true, onClick: () => setDeleteTarget(a) },
                    ]}
                  />
                </div>
              </section>
            );
          })}
          {filter !== "problems" && !q && lastPage && rows.length - shownPage * PAGE_SIZE < PAGE_SIZE && (
            <button
              type="button"
              onClick={() => navigate("/integrations/marketplaces/new")}
              className={cn(
                "flex min-h-[188px] flex-col items-center justify-center gap-2 rounded-card border border-dashed border-[var(--border)] p-4 text-center",
                "text-[var(--text-secondary)] hover:border-[var(--primary)] hover:bg-[var(--bg-hover)]"
              )}
            >
              <Plus size={22} aria-hidden />
              <span className="text-[15px] font-semibold text-[var(--text-primary)]">Connect an exchange</span>
              <span className="text-xs text-[var(--text-muted)]">
                {accounts.length ? connectable.join(" · ") : "Needed only for live trading. It takes about two minutes."}
              </span>
            </button>
          )}
        </div>
      )}
      <Pager page={shownPage} pageSize={PAGE_SIZE} total={rows.length} onPage={setPage} className="" />

      {openNew && <NewAccountModal onClose={() => navigate("/integrations/marketplaces", { replace: true })} />}

      <ConfirmDialog
        open={Boolean(deleteTarget)}
        title={`Delete "${deleteTarget?.name}"?`}
        description={
          deleteLive
            ? `${deleteLive} live ${deleteLive === 1 ? "strategy uses" : "strategies use"} this account and will stop trading. The keys are removed.`
            : "The account and its keys are removed."
        }
        loading={busyId === deleteTarget?.id}
        onCancel={() => setDeleteTarget(null)}
        onConfirm={() => {
          const t = deleteTarget;
          if (!t) return;
          void run(t.id, () => api.commands.deleteAccount({ id: t.id }), "Delete failed").then(() => setDeleteTarget(null));
        }}
      />
    </div>
  );
}
